/**
 * FiscalizacionOT.jsx — Página dedicada para llenar el formulario de fiscalización
 * de una OT. Recibe otId por URL param. Carga automáticamente el solution_type
 * desde el nodo del árbol asociado a la OT.
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import FiscalizacionForm from "../components/FiscalizacionForm";
import api from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";

export default function FiscalizacionOT() {
  const { otId } = useParams();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [otInfo, setOtInfo] = useState(null);
  const [stId, setStId] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // Intenta cargar la OT desde smart-inbox para obtener el solution_type_id
        // via el nodo del árbol
        const { data } = await api.get(`/v1/mesa/ots/${otId}`).catch(() => ({ data: null }));
        if (data) {
          setOtInfo(data);
          // Si la OT tiene un solution_tree_node_id → resolver a solution_type_id
          if (data.solution_type_id) {
            setStId(data.solution_type_id);
          } else if (data.solution_tree_node_id) {
            const nodeRes = await api.get(`/v1/solution-tree`).catch(() => null);
            const nodes = nodeRes?.data?.tree || [];
            const findNode = (arr, id) => {
              for (const n of arr) {
                if (n.id === id) return n;
                const r = findNode(n.children || [], id);
                if (r) return r;
              }
            };
            const node = findNode(nodes, data.solution_tree_node_id);
            if (node?.solution_type_id) setStId(node.solution_type_id);
          }
        }
      } catch (e) { toast.error("Error cargando OT"); }
      finally { setLoading(false); }
    })();
  }, [otId]);

  return (
    <Layout>
      <div className="max-w-3xl mx-auto p-4">
        <button onClick={() => nav(-1)} className="text-sm text-[#0A3747] mb-3 flex items-center gap-1" data-testid="back-btn">
          <ArrowLeft size={14} /> Volver
        </button>
        <div className="mb-3 bg-[#E0E621]/20 border border-[#E0E621] rounded p-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#6B7000]">Fiscalización de OT</div>
          <div className="text-lg font-bold text-[#0A3747]">{otId}</div>
          {otInfo?.task_name && <div className="text-xs text-gray-600">{otInfo.task_name}</div>}
        </div>

        {loading ? (
          <div className="text-center p-10"><Loader2 className="animate-spin mx-auto" /></div>
        ) : !stId ? (
          <div className="bg-yellow-50 border border-yellow-300 rounded p-4 text-sm">
            ⚠️ Esta OT no tiene un <b>Tipo de Solución</b> asignado (o el nodo del árbol no está vinculado).
            <br /><br />
            Selecciona manualmente el Tipo de Solución para poder fiscalizar:
            <ManualSTPicker onPick={setStId} />
          </div>
        ) : (
          <FiscalizacionForm otId={otId} solutionTypeId={stId} />
        )}
      </div>
    </Layout>
  );
}

function ManualSTPicker({ onPick }) {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState("");
  useEffect(() => { api.get("/v1/solution-types?limit=500").then(({ data }) => setItems(data.items || [])); }, []);
  return (
    <div className="mt-3 flex gap-2">
      <select value={selected} onChange={(e) => setSelected(e.target.value)} className="flex-1 px-2 py-1.5 border rounded text-sm" data-testid="manual-st-select">
        <option value="">— Selecciona un Tipo de Solución —</option>
        {items.map((st) => <option key={st.id} value={st.id}>{st.code} · {st.name}</option>)}
      </select>
      <button onClick={() => selected && onPick(selected)} disabled={!selected} className="px-3 py-1.5 bg-[#0A3747] text-white rounded text-xs font-bold disabled:opacity-50" data-testid="manual-st-confirm">Usar este</button>
    </div>
  );
}
